package com.bikompanion.data.cache

/**
 * The smallest durable key/value store each platform can offer.
 *
 * Deliberately not a database: what's kept is two or three JSON blobs read once at startup
 * and overwritten wholesale, which is a file on the JVM targets and `localStorage` in the
 * browser. Neither reads nor writes may throw — a cache that can take the app down with it
 * is worse than no cache — so every implementation swallows its own failures and a read that
 * can't be served simply returns null.
 *
 * An interface rather than an `expect class` so the handful of things that persist state can
 * be exercised against an in-memory store instead of the user's real disk.
 */
interface LocalStore {
    fun read(key: String): String?

    fun write(
        key: String,
        value: String,
    )

    /** Drops [key], if it is there. Storage that only ever grows fills up and then stops working. */
    fun remove(key: String)

    /**
     * Every key currently stored, so an owner can clean up after itself.
     *
     * Needed because entries written under an older [BikeDataCache] version can no longer be
     * addressed once the version changes — without this they are unreachable and permanent,
     * which on the web is `localStorage` quota nobody can reclaim.
     */
    fun keys(): List<String>
}

/** The store this platform actually persists to. Bound once, in [CacheProviders]. */
internal expect fun createLocalStore(): LocalStore

/**
 * Keys reach the filesystem, so anything that could climb out of the cache directory or that
 * a filesystem would reject is flattened first. Network ids are slugs today; this is here so
 * a feed that starts publishing something stranger can't turn a cache write into a path.
 */
internal fun cacheKey(vararg parts: String): String =
    parts.joinToString("-") { part -> part.map { if (it.isLetterOrDigit()) it else '_' }.joinToString("") }
